/**
 * The design-system sandbox's message protocol — the ONLY way the editor and
 * uploaded design-system code talk. Nothing but plain data crosses: widget
 * descriptions go in, HTML strings and reports come out. Each side validates
 * everything it receives from the other.
 */
import { isDsWidgetKind, type DsWidgetKind } from "../theme/dsWidgetElement.js";

export interface DsWidgetDescription {
  readonly key: string;
  readonly contentType: DsWidgetKind;
  readonly content: Record<string, unknown>;
}

export type SandboxRequest =
  | { readonly id: number; readonly type: "load"; readonly code: string }
  | {
      readonly id: number;
      readonly type: "render";
      readonly widgets: ReadonlyArray<DsWidgetDescription>;
    }
  | { readonly id: number; readonly type: "conform" };

export type SandboxResponse =
  | { readonly id: number; readonly ok: true; readonly result: unknown }
  | { readonly id: number; readonly ok: false; readonly error: string };

export interface LoadResult {
  readonly id: string;
  readonly name: string;
}

/** Sent once by the sandbox when its runtime is listening. */
export const READY_TYPE = "wb-ds-sandbox-ready";

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);

export function isSandboxRequest(v: unknown): v is SandboxRequest {
  if (!isRecord(v) || typeof v["id"] !== "number") return false;
  switch (v["type"]) {
    case "load":
      return typeof v["code"] === "string";
    case "conform":
      return true;
    case "render":
      return (
        Array.isArray(v["widgets"]) &&
        v["widgets"].every(
          (w) =>
            isRecord(w) &&
            typeof w["key"] === "string" &&
            typeof w["contentType"] === "string" &&
            isDsWidgetKind(w["contentType"]) &&
            isRecord(w["content"]),
        )
      );
    default:
      return false;
  }
}

export function isSandboxResponse(v: unknown): v is SandboxResponse {
  if (!isRecord(v) || typeof v["id"] !== "number") return false;
  return v["ok"] === true
    ? "result" in v
    : v["ok"] === false && typeof v["error"] === "string";
}
